<template>
  <div class="p-4 flex flex-col">
    <loading
      :active.sync="savingSubdomain"
      :is-full-page="fullPageLoader"
      :color="loaderColor"
    ></loading>
    <!-- Current hosting info  -->
    <div>
      <div class="flex items-center">
        <div class="w-1/3">
          <gd-button 
            variant="outline"
            size="auto"
            @click="$emit('clickedBackButton')"
          >
            {{ $t('frontend_back') }}
          </gd-button>
        </div>
        <div class="flex flex-col w-1/3 justify-center items-center">
          <div class="font-bold">
            <span class="font-bold">{{ $t('frontend_application') }} : </span>
          </div>
          <div class="flex gap-2">
            <span class="mt-2">{{ application }}</span>
            <img :src="getAppLogo(application)" alt="" class="w-8 h-8" />
          </div>
        </div>
      </div>

      <table
        class="min-w-max w-full table-auto mt-8 border-b border-grey-200"
        :key="tableKey"
      >
        <thead>
          <tr
            class="
              text-grey-500 text-sm
              leading-normal
              border-b border-grey-200
            "
          >
            <th class="py-3 px-6 text-left">{{ $t('frontend_subdomain') }}</th>
            <th class="py-3 px-6 text-left">{{ $t('frontend_type') }}</th>
            <th class="py-3 px-6 text-left">{{ $t('frontend_actions') }}</th>
          </tr>
        </thead>
        <template v-for="subdomain in appSubdomains">
          <tbody
            class="text-grey-800 text-sm -mx-4"
            v-if="!selectedSubdomain || selectedSubdomain.id !== subdomain.id"
            :key="subdomain.id"
          >
            <tr class="border-grey-200">
              <td class="py-3 px-6">
                https://<span class="font-bold">{{ subdomain.subdomain }}</span
                >.{{ domain.domain_name }}
              </td>
              <td class="py-3 px-6 text-left">
                {{ fetchApplicationName(subdomain.application) }}
              </td>
              <td class="py-3 px-6 text-left">
                <div class="flex flex-row justify-left">
                  <gd-button
                    variant="primary"
                    size="icon"
                    class="mr-2"
                    @click="
                      selectedSubdomain = subdomain;
                      openEditEditor = true;
                    "
                  >
                    <edit-icon class="fill-current w-4 h-4" />
                  </gd-button>

                  <gd-button
                    variant="primary"
                    size="icon"
                    class="mr-2"
                    :loading="
                      deletingSubdomain && deletingSubdomain.id === subdomain.id
                    "
                    @click="confirmDeleteSubdomain(subdomain)"
                  >
                    <trash-icon class="fill-current w-4 h-4" />
                  </gd-button>
                </div>
              </td>
            </tr>
          </tbody>
          <domain-subdomain-editor
            v-else-if="
              selectedSubdomain &&
                selectedSubdomain.id === subdomain.id &&
                openEditEditor
            "
            :key="subdomain.id"
            :current="subdomain"
            :saving="savingSubdomain"
            :editing="openEditEditor"
            @cancel="selectedSubdomain = undefined"
            @save="data => updateSubdomain({ id: subdomain.id, ...data })"
            class="my-3"
          />
        </template>
        <domain-subdomain-editor
          :saving="savingSubdomain"
          :editing="openAddEditor"
          @save="addSubdomain"
          @showEditor="openAddEditor = true"
          @cancel="openAddEditor = false"
          class="my-3"
        />
      </table>

      <div v-if="domain.type == 2" class="mt-4">
        <div v-if="application == 'GroovePages' || application == 'GrooveBlog'">
          <span class="text-red font-bold">{{ $t('frontend_note') }}: </span>
          {{ $t('frontend_subdomains_please_create_cname_to') }}
          <span class="font-bold">host10.groovepages.com</span>
          {{ $t('frontend_subdomains_for_the_subdomains_listed_above') }}
        </div>
        <div v-else-if="application == 'GrooveSell'">
          <span class="text-red font-bold">{{ $t('frontend_note') }}: </span>
          {{ $t('frontend_subdomains_please_create_cname_to') }}
          <span class="font-bold">get-direct.groovesell.com</span>
          {{ $t('frontend_subdomains_if_using_cloudflare_or_to') }}
          <span class="font-bold">get.groovesell.com</span>
          {{ $t('frontend_subdomains_if_using_cloudflare') }}
        </div>
        <div v-else-if="application == 'GrooveMember'">
          <span class="text-red">{{ $t('frontend_note') }}: </span>
          {{ $t('frontend_subdomains_please_create_cname_to') }}
          <span class="font-bold">get-direct.groovemember.net</span>
          {{ $t('frontend_subdomains_if_using_cloudflare_or_to') }}
          <span class="font-bold">get.groovemember.net</span>
          {{ $t('frontend_subdomains_if_using_cloudflare') }}
        </div>
      </div>
    </div>
    <confirm-modal
      @close="deletingSubdomain = undefined"
      @confirm="deleteSubdomain"
      name="delete-record"
    />
  </div>
</template>

<script>
import Notify from '../notify';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import { mapGetters } from 'vuex';
import EditIcon from '../assets/icons/pen.svg';
import TrashIcon from '../assets/icons/trash.svg';
import themeConfig from '../themeConfig.js';
import GdButton from './Button';
import ConfirmModal from './ConfirmModal';
import DomainSubdomainEditor from './DomainSubdomainEditor';
import GdModal from './Modal';
import TextInput from './TextInput';

export default {
  name: 'Subdomains',
  components: {
    GdModal,
    ConfirmModal,
    DomainSubdomainEditor,
    GdButton,
    TextInput,
    Loading,
    EditIcon,
    TrashIcon
  },
  computed: {
    ...mapGetters({
      subdomains: 'subdomains/subdomains',
      domain: 'subdomains/domain',
      application: 'subdomains/application'
    }),
    appSubdomains() {
      return this.$store.getters['subdomains/subdomains'].filter(
        (value, index) => {
          switch (this.application) {
            case 'GroovePages':
              return value.application == 'pages';
            case 'GrooveSell':
              return (
                value.application == 'sell-funnel' ||
                value.application == 'sell-tracking'
              );
            case 'GrooveMember':
              return (
                value.application == 'membership' ||
                value.application == 'portal'
              );
            case 'GrooveBlog':
              return value.application == 'blog';
            case 'GrooveKart':
              return value.application == 'kart';
            case 'Groove.ai':
              return value.application == 'ai';
            default:
              break;
          }
        }
      );
    },
    brandApps() {
      const brandData = this.$store.getters.brandData;
      if (brandData) {
        return brandData.apps.reduce((acc, app) => {
          if (app.slug) acc[app.slug] = app;
          return acc;
        }, {});
      }
      return null;
    }
  },
  data() {
    return {
      tableKey: 1,
      selectedPanel: '',
      openAddEditor: false,
      openEditEditor: false,
      setUpDomain: false,
      setUpCustomDomain: false,
      showCname: false,
      setupGrooveDomain: false,
      errorMsg: '',
      domainColumns: [
        this.$t('frontend_type'),
        this.$t('frontend_name'),
        this.$t('frontend_content'),
        'TTL',
        'Proxy'
      ],
      newSubDomain: '',
      showCreateDomain: false,
      showSubdomainEditor: false,
      savingSub: false,
      loadingDomains: false,
      loadingRecords: false,
      savingSubdomain: false,
      togglingProxy: undefined,
      selectedSubdomain: undefined,
      deletingSubdomain: undefined,
      deletingDomain: undefined,
      selectedDomain: undefined,
      newDomain: {
        domain: '',
        hosted: true,
        platform: 'pages'
      },
      addingDomain: undefined,
      grooveSellLegacyDomains: [],
      fullPageLoader: true,
      loaderColor: themeConfig.appPrimaryColor
    };
  },
  created() {},
  methods: {
    showError(e) {
      console.error(e);
      if (e) {
        if (e && e.response && e.response.data.errors) {
          return Notify.fail(
            e.response.data.errors[0].message,
            e.response.data.errors.map(({ message }) => message).join('. ')
          );
        }
        return Notify.fail(e.message);
      }
    },

    getAppLogo(application) {
      switch (application) {
        case 'GroovePages':
          return this.brandApps?.['pages']?.logo;
        case 'GrooveSell':
          return this.brandApps?.['sell']?.logo;
        case 'GrooveMember':
          return this.brandApps?.['member']?.logo;
        case 'GrooveBlog':
          return this.brandApps?.['blog']?.logo;
        case 'GrooveKart':
          return this.brandApps?.['kart']?.logo;
        case 'Groove.ai':
          return this.brandApps?.['ai']?.logo;
      }
    },

    fetchApplicationName(application) {
      switch (application) {
        case 'pages':
          return 'GroovePages';
        case 'sell-funnel':
          return 'GrooveSell Branding';
        case 'sell-tracking':
          return 'GrooveSell Tracking';
        case 'membership':
          return 'GrooveMember Membership';
        case 'portal':
          return 'GrooveMember Portal';
        case 'blog':
          return 'GrooveBlog';
        case 'kart':
          return 'GrooveKart';
        case 'ai':
          return 'Groove.ai';
        default:
          break;
      }

      return 'GroovePages';
    },

    async addSubdomain(data) {
      try {
        this.savingSubdomain = true;
        const status = await this.$store.dispatch('subdomains/addSubdomain', {
          data
        });
        if (status == 'success') {
          Notify.success('Successfully added subdomain');
        } else if (status == 'duplicate') {
          Notify.fail('Subdomain unavailable');
        }
        this.showSubdomainEditor = false;
      } catch (e) {
        this.showError(e);
      } finally {
        this.savingSubdomain = false;
        this.selectedSubdomain = undefined;
        this.openAddEditor = false;
      }
    },
    async updateSubdomain(data) {
      try {
        this.savingSubdomain = true;
        const status = await this.$store.dispatch(
          'subdomains/updateSubdomain',
          {
            data
          }
        );
        if (status == 'success') {
          Notify.success('Updated subdomain successfully');
        } else if (status == 'duplicate') {
          Notify.fail('Subdomain unavailable');
        }

        this.showSubdomainEditor = false;
      } catch (e) {
        this.showError(e);
      } finally {
        this.selectedSubdomain = undefined;
        this.savingSubdomain = false;
        this.openAddEditor = false;
        this.tableKey += 1;
      }
    },
    confirmDeleteSubdomain(record) {
      this.deletingSubdomain = record;
      this.$root.$emit('modal-open', 'delete-record');
    },
    async deleteSubdomain() {
      try {
        await this.$store.dispatch('subdomains/deleteSubdomain', {
          id: this.deletingSubdomain.id
        });
        Notify.success('Deleted DNS Record Successfully');
        this.deletingSubdomain = undefined;
      } catch (e) {
        this.showError(e);
      }
    }
  }
};
</script>
